import { apiGetJSON, loadAPIKey, loadSelectedAccountID, saveAPIKey, saveSelectedAccountID } from "/client.js";
import { isSameOriginAuthToken } from "/core/client-auth.js";
import {
  createPWABadgeState,
  syncPWABadge
} from "/core/pwa-badge.js";
import {
  canPromptPWAInstall,
  createPWAInstallState,
  promptPWAInstall,
  pwaInstallStatusText,
  startPWAInstall
} from "/core/pwa-install.js";
import { captureRequest, createRequestCoordinator } from "/core/request-coordinator.js";
import { setStatus as setSurfaceStatus } from "/core/surface-host.js";
import {
  canCancel,
  decisionKindLabel,
  resolveRunState,
  sortTurnsDescending,
} from "/domain/conversation.js";
import { normalizeWorkspaceDiagnostics } from "/domain/diagnostics.js";
import { createTurnDetailState } from "/domain/turn-detail.js";
import {
  escapeHTML,
  formatTimestamp,
  renderContextRow,
  timeAgo,
  truncateIDText as renderTruncatedIDText
} from "/render/ui-helpers.js";
import {
  handleAdminRouteChange,
  loadAdminBaseData,
  refreshAdminCollections,
  resetAdminThreadDetail,
  resetAdminTurnDetail,
  startAdminLiveResources,
  stopAdminLiveResources,
} from "/admin-shell/routes.js";
import {
  handleAdminClick,
  handleAdminFieldInput,
  handleAdminSubmit
} from "/admin-shell/actions.js";
import { handleWorkspaceContentDragDrop } from "/workspace-content-component.js";
import { renderAdminShell } from "/admin-shell/render.js";
import { renderTurnRow as renderAdminTurnRow } from "/admin-pages/turns.js";
import { createRouter } from "/router.js";
import {
  createThreadComponentState,
  handleThreadComponentDragDrop,
  renderThreadComponent,
  threadMergeStateSuffix
} from "/thread-component.js";
import { renderHarnessThreadDetailDrawer } from "/thread-component/harness/detail.js";
import { renderLLMThreadDetailDrawer } from "/thread-component/llm/detail.js";
import {
  renderTurnDetailComponent,
  renderTurnDetailDrawer,
} from "/turn-detail-component.js";
import {
  createWorkspaceContentState,
  resetWorkspaceContentState,
  renderWorkspaceContentPanel
} from "/workspace-content-component.js";
import { captureFormDraft } from "/form-draft.js";

const NAV_ITEMS = [
  { id: "threads", label: "Th", title: "Threads", href: "/threads" },
  { id: "agents", label: "Ag", title: "Agents", href: "/agents" },
  { id: "workspaces", label: "Ws", title: "Workspaces", href: "/workspaces" }
];

const MODELS_NAV_ITEMS = [
  { id: "models", label: "Mo", title: "Models", href: "/models" }
];

const DIAGNOSTIC_NAV_ITEMS = [
  { id: "turns", label: "Dx", title: "Diagnostics", href: "/turns" }
];

const BREADCRUMB_TITLES = {
  threads: "Threads",
  "thread-detail": "Thread",
  turns: "Diagnostics",
  "turn-detail": "Turn Trace",
  agents: "Agents",
  models: "Models",
  workspaces: "Workspaces"
};

const state = {
  route: { id: "threads", pathname: "/threads", params: {} },
  apiKey: "",
  accounts: [],
  selectedAccountID: "",
  accountSwitcherOpen: false,
  accountSwitcherPlacement: "",
  accountSwitcherPreviewID: "",
  accountSwitcherQuery: "",
  authInput: "",
  authStatus: { tone: "idle", message: "" },
  status: { tone: "idle", message: "Enter a batch-1 bearer API key to unlock the console." },
  loading: false,
  agents: [],
  harnesses: [],
  workspaces: [],
  threads: [],
  turns: [],
  promotions: [],
  pendingDecisions: [],
  inferenceEnabled: false,
  inferenceCredentials: [],
  inferenceRoutes: [],
  inferenceTraffic: [],
  workspaceDiagnosticsByID: {},
  threadDetail: createThreadComponentState(),
  turnDetail: createTurnDetailState(),
  editingAgentID: "",
  editingWorkspaceID: "",
  editingInferenceCredentialID: "",
  editingInferenceRouteID: "",
  router: null,
  decisionDrafts: {},
  forms: {
    agent: defaultAgentDraft(),
    workspace: defaultWorkspaceDraft(),
    thread: defaultThreadDraft(),
    turn: defaultTurnDraft(),
    promotion: defaultPromotionDraft(),
    inferenceCredential: defaultInferenceCredentialDraft(),
    inferenceRoute: defaultInferenceRouteDraft(),
    inferencePlayground: defaultInferencePlaygroundDraft()
  },
  formErrors: { agent: "", workspace: "", thread: "", turn: "", inferenceCredential: "", inferenceRoute: "", inferencePlayground: "" },
  live: { pollHandle: null },
  settingsOpen: false,
  detailDrawerOpen: false,
  threadFilter: "all",
  threadCreateOpen: false,
  agentCreateOpen: false,
  workspaceCreateOpen: false,
  inferenceCredentialFormOpen: false,
  inferenceRouteFormOpen: false,
  inferenceTrafficFilter: "all",
  inferenceTrafficExpanded: false,
  inferencePlaygroundShowRawJSON: false,
  inferenceCredentialRefManuallyEdited: false,
  diagnosticFilter: "all",
  diagnosticCreateOpen: false,
  reviewConfigWorkspaceID: "",
  pushWorkspaceID: "",
  workspaceContent: createWorkspaceContentState(),
  promotionConfirm: null,
  threadContentPanelOpen: false,
  inferencePlaygroundLoading: false,
  inferencePlaygroundResponse: null,
  navCollapsed: localStorage.getItem("nav-collapsed") === "1",
  navCollapsedBeforeThread: null,
  pwaBadge: createPWABadgeState(),
  pwaInstall: createPWAInstallState(),
  requests: {
    route: createRequestCoordinator(),
    collections: createRequestCoordinator()
  }
};

const root = document.getElementById("app");

function adminThreadComponentOptions() {
  const request = captureRequest(state.requests.route);
  return {
    apiKey: state.apiKey,
    render,
    setStatus,
    refreshCollections() {
      return loadBaseData({ request });
    },
    request,
    signal: request?.signal || null,
    resolvedBy: "builtin-web",
    onThreadReplaced(threadID) {
      state.router.navigate(`/threads/${encodeURIComponent(threadID)}`);
    }
  };
}

function adminTurnDetailOptions() {
  const request = captureRequest(state.requests.route);
  return {
    apiKey: state.apiKey,
    agents: state.agents,
    workspaces: state.workspaces,
    decisionDrafts: state.decisionDrafts,
    lookupTurn: lookupDecisionTurn,
    render,
    request,
    setStatus,
    refreshCollections() {
      return refreshTurnCollections();
    },
    onTurnMerged(turn) {
      mergeTurnsIntoGlobalList([turn]);
    },
    onTurnStateChange(turnID, nextState) {
      syncTurnStateFromEvent(turnID, nextState);
    }
  };
}

function adminRouteOptions() {
  return {
    render,
    setStatus,
    resetWorkspaceContentPanel,
    lookupByID,
    lookupWorkspaceDiagnostics,
    agentDraftFromItem,
    workspaceDraftFromItem,
    defaultAgentDraft,
    defaultWorkspaceDraft,
    syncAgentDraftDefaults,
    hasActiveWork,
    sameTurnSummary,
    sameThreadSummary,
    samePromotionSummary,
    sameDecisionSummary,
    adminThreadComponentOptions,
    adminTurnDetailOptions,
    renderAuthDialog,
    handleAuthFailure
  };
}

function adminActionOptions() {
  return {
    render,
    setStatus,
    promptPWAInstall() {
      return promptPWAInstall(state.pwaInstall, { onChange: render });
    },
    handleRouteChange,
    validateAPIKey,
    loadBaseData,
    stopLiveResources,
    startLiveResources,
    resetThreadDetail,
    resetTurnDetail,
    resetWorkspaceContentPanel,
    selectAccount,
    toggleTheme,
    formKeyForID,
    normalizeAgentDraft,
    normalizeThreadDraft,
    defaultAgentDraft,
    defaultWorkspaceDraft,
    defaultThreadDraft,
    defaultTurnDraft,
    requireAPIKey,
    captureDraftFromForm,
    parseRuntimeConfig,
    buildAgentRuntimeConfig,
    validateAgentDraft,
    validateWorkspaceDraft,
    validateThreadDraft,
    validateInferenceCredentialDraft,
    validateInferenceRouteDraft,
    agentDraftFromItem,
    workspaceDraftFromItem,
    inferenceCredentialDraftFromItem,
    inferenceRouteDraftFromItem,
    defaultInferenceCredentialDraft,
    defaultInferenceRouteDraft,
    defaultInferencePlaygroundDraft,
    slugify,
    loadWorkspaceDiagnostics,
    lookupWorkspaceDiagnostics,
    clearWorkspaceDiagnostics,
    lookupByID,
    lookupDecision,
    adminThreadComponentOptions,
    adminTurnDetailOptions,
    adminWorkspaceContentOptions
  };
}

async function selectAccount(accountID) {
  const normalizedAccountID = String(accountID || "").trim();
  state.accountSwitcherOpen = false;
  state.accountSwitcherPlacement = "";
  state.accountSwitcherPreviewID = "";
  state.accountSwitcherQuery = "";
  if (normalizedAccountID === String(state.selectedAccountID || "").trim()) {
    render();
    return;
  }
  state.selectedAccountID = saveSelectedAccountID(normalizedAccountID);
  stopLiveResources();
  resetThreadDetail();
  resetTurnDetail();
  resetWorkspaceContentPanel();
  setStatus("Switching account...", "busy");
  await handleRouteChange(state.route);
  setStatus("Account selected.", "ok");
}

document.addEventListener("DOMContentLoaded", () => {
  initTheme();
  startPWAInstall(state.pwaInstall, {
    onChange: render,
    onInstall() {
      setStatus("App installed.", "ok");
    }
  });
  state.apiKey = loadAPIKey().trim();
  state.selectedAccountID = loadSelectedAccountID().trim();
  state.authInput = isSameOriginAuthToken(state.apiKey) ? "" : state.apiKey;
  state.router = createRouter(handleRouteChange);
  root.addEventListener("click", handleClick);
  root.addEventListener("submit", handleSubmit);
  root.addEventListener("input", handleFieldInput);
  root.addEventListener("change", handleFieldInput);
  root.addEventListener("keydown", handleComposerKeydown);
  root.addEventListener("dragover", handleDrag);
  root.addEventListener("dragleave", handleDrag);
  root.addEventListener("drop", handleDrag);

  if (window.location.pathname === "/") {
    state.router.navigate("/threads", { replace: true });
    return;
  }
  state.router.start();
});

// ── Theme ──

const THEME_KEY = "lytebulb:runtime:theme";

function initTheme() {
  const stored = localStorage.getItem(THEME_KEY);
  const prefersDark = matchMedia("(prefers-color-scheme: dark)").matches;
  const dark = stored ? stored === "dark" : prefersDark;
  applyTheme(dark);
  if (!stored) {
    matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", (e) => applyTheme(e.matches));
  }
}

function applyTheme(dark) {
  document.documentElement.classList.toggle("dark", dark);
}

function toggleTheme() {
  const isDark = document.documentElement.classList.toggle("dark");
  localStorage.setItem(THEME_KEY, isDark ? "dark" : "light");
}

// ── Route handling ──

async function handleRouteChange(route) {
  await handleAdminRouteChange(state, route, adminRouteOptions());
}

async function loadBaseData(options = {}) {
  return loadAdminBaseData(state, options, adminRouteOptions());
}

async function validateAPIKey(apiKey) {
  await apiGetJSON(apiKey, "/v1/agents?limit=1");
}

function handleAuthFailure(error) {
  saveAPIKey("");
  state.apiKey = "";
  state.authInput = "";
  state.authStatus = { tone: "error", message: authFailureMessage(error) };
  state.status = { tone: "error", message: state.authStatus.message };
  state.loading = false;
  state.settingsOpen = false;
  stopLiveResources();
  render();
}

function authFailureMessage(error) {
  if (error?.status === 401) {
    return "Invalid API key.";
  }
  return error?.message || "Authentication failed.";
}

async function loadWorkspaceDiagnostics(workspaceID, options = {}) {
  const normalizedWorkspaceID = String(workspaceID || "").trim();
  if (!normalizedWorkspaceID || !state.apiKey) {
    return null;
  }
  if (!options.force && state.workspaceDiagnosticsByID[normalizedWorkspaceID]) {
    return state.workspaceDiagnosticsByID[normalizedWorkspaceID];
  }
  const diagnostics = normalizeWorkspaceDiagnostics(await apiGetJSON(
    state.apiKey,
    `/v1/workspaces/${encodeURIComponent(normalizedWorkspaceID)}/diagnostics`,
    { signal: options.signal }
  ));
  state.workspaceDiagnosticsByID[normalizedWorkspaceID] = diagnostics;
  return diagnostics;
}

function lookupWorkspaceDiagnostics(workspaceID) {
  return state.workspaceDiagnosticsByID[String(workspaceID || "").trim()] || null;
}

function clearWorkspaceDiagnostics(workspaceID) {
  delete state.workspaceDiagnosticsByID[String(workspaceID || "").trim()];
}

// ── Live resources ──

function startLiveResources(route) {
  startAdminLiveResources(state, route, adminRouteOptions());
}

async function refreshTurnCollections() {
  return refreshAdminCollections(state, adminRouteOptions());
}

function stopLiveResources() {
  stopAdminLiveResources(state);
}

function resetTurnDetail() {
  resetAdminTurnDetail(state);
}

function resetThreadDetail() {
  resetAdminThreadDetail(state);
}

// ── Form submissions ──

async function handleSubmit(event) {
  await handleAdminSubmit(state, event, adminActionOptions());
}

function resetWorkspaceContentPanel() {
  resetWorkspaceContentState(state.workspaceContent);
}

function adminWorkspaceContentOptions(workspaceItem) {
  const request = captureRequest(state.requests.route);
  return {
    apiKey: state.apiKey,
    render,
    setStatus,
    refreshCollections() {
      return loadBaseData({ request });
    },
    request,
    workspaceItem
  };
}

async function toggleWorkspaceContentPanel(workspaceID) {
  if (!workspaceID) return;
  if (state.workspaceContent.workspaceID === workspaceID) {
    resetWorkspaceContentPanel();
    render();
    return;
  }
  const selectedWorkspace = lookupByID(state.workspaces, workspaceID);
  if (!selectedWorkspace) throw new Error("Workspace not found.");
  cancelWorkspaceContentRequests(state.workspaceContent);
  state.workspaceContent = createWorkspaceContentState();
  state.workspaceContent.workspaceID = workspaceID;
  state.workspaceContent.refInput = String(selectedWorkspace.default_branch || "");
  render();
  await loadWorkspaceContentTree(state.workspaceContent, state.apiKey, workspaceID, {
    ref: state.workspaceContent.refInput,
    path: "",
    render,
    request: captureRequest(state.requests.route)
  });
}
// ── Input handling ──

function handleFieldInput(event) {
  const target = event.target;
  if (target instanceof HTMLInputElement && target.name === "account_search" && target.closest("[data-account-switcher]")) {
    const switcher = target.closest("[data-account-switcher]");
    const placement = switcher?.getAttribute("data-account-switcher") || state.accountSwitcherPlacement || "header";
    const selectionStart = target.selectionStart;
    const selectionEnd = target.selectionEnd;
    state.accountSwitcherQuery = target.value;
    state.accountSwitcherPlacement = placement;
    render();
    restoreAccountSearchFocus(placement, selectionStart, selectionEnd);
    return;
  }
  handleAdminFieldInput(state, event, adminActionOptions());
}

function restoreAccountSearchFocus(placement, selectionStart, selectionEnd) {
  const restore = () => {
    const switchers = Array.from(root.querySelectorAll("[data-account-switcher]"));
    const switcher = switchers.find((item) => item.getAttribute("data-account-switcher") === placement);
    const input = switcher?.querySelector("input[name='account_search']");
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    input.focus({ preventScroll: true });
    const start = Number.isInteger(selectionStart) ? selectionStart : input.value.length;
    const end = Number.isInteger(selectionEnd) ? selectionEnd : start;
    try {
      input.setSelectionRange(start, end);
    } catch {
      // Some input types may reject selection ranges; focus is still the important behavior.
    }
  };
  restore();
  requestAnimationFrame(restore);
}

function handleComposerKeydown(event) {
  if (!(event.target instanceof HTMLTextAreaElement)) return;
  if (event.target.name !== "prompt") return;
  const form = event.target.closest("#thread-message-form");
  if (!form) return;
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    form.requestSubmit();
  }
}

// ── Click handling ──

async function handleClick(event) {
  if (await handleAccountSwitcherClick(event)) {
    return;
  }
  await handleAdminClick(state, event, adminActionOptions());
}

async function handleAccountSwitcherClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) {
    return false;
  }
  const toggle = target.closest("[data-action='toggle-account-switcher']");
  if (toggle) {
    const switcher = toggle.closest("[data-account-switcher]");
    const placement = switcher?.getAttribute("data-account-switcher") || "header";
    const isSameOpen = state.accountSwitcherOpen && state.accountSwitcherPlacement === placement;
    state.accountSwitcherOpen = !isSameOpen;
    state.accountSwitcherPlacement = state.accountSwitcherOpen ? placement : "";
    state.accountSwitcherPreviewID = state.accountSwitcherOpen ? selectedAccountOptionValue(normalizedAccountOptions()) : "";
    if (!state.accountSwitcherOpen) {
      state.accountSwitcherQuery = "";
    }
    render();
    return true;
  }
  if (target.closest("[data-action='close-account-switcher']")) {
    state.accountSwitcherOpen = false;
    state.accountSwitcherPlacement = "";
    state.accountSwitcherPreviewID = "";
    state.accountSwitcherQuery = "";
    render();
    return true;
  }
  if (target.closest("[data-action='clear-account-search']")) {
    state.accountSwitcherQuery = "";
    render();
    return true;
  }
  const previewButton = target.closest("[data-account-preview]");
  if (previewButton) {
    state.accountSwitcherPreviewID = previewButton.getAttribute("data-account-preview") || "";
    render();
    return true;
  }
  const accountButton = target.closest("[data-account-switch]");
  if (accountButton) {
    const accountID = accountButton.getAttribute("data-account-switch") || "";
    await selectAccount(accountID);
    return true;
  }
  if (state.accountSwitcherOpen && !target.closest("[data-account-switcher]")) {
    state.accountSwitcherOpen = false;
    state.accountSwitcherPlacement = "";
    state.accountSwitcherPreviewID = "";
    state.accountSwitcherQuery = "";
  }
  return false;
}

// ── Drag-and-drop ──

function handleDrag(event) {
  if (state.threadDetail?.thread) {
    if (handleThreadComponentDragDrop(state.threadDetail, event, adminActionOptions())) {
      return;
    }
  }
  if (state.workspaceContent?.workspaceID) {
    const workspaceItem = lookupByID(state.workspaces, state.workspaceContent.workspaceID);
    handleWorkspaceContentDragDrop(state.workspaceContent, event, adminWorkspaceContentOptions(workspaceItem));
  }
}

// ── Render ──

function render() {
  renderAdminShell(root, state, {
    renderNavRail,
    renderBreadcrumbBar,
    renderLockedState,
    renderRoutePage: renderPage,
    renderThreadDetailPage,
    renderThreadContentPanel,
    renderDetailDrawer,
    renderSettingsPopover,
    renderAuthDialog,
    lookupByID,
    lookupWorkspaceDiagnostics,
    harnessesForAdapter,
    lookupHarnessForDraft,
    staleAttachedServerURL,
    harnessDisplayLabel,
    formatHarnessNote,
    truncatePath,
    workspacePrimaryAction,
    workspacePrimaryLocation,
    workspaceStateSummary,
    renderWorkspacePushStatus,
    renderWorkspaceCommitHistory,
    renderWorkspaceReviewForm,
    renderWorkspacePushPanel,
    renderWorkspaceContentPanelForList,
    threadTitle,
    threadPreviewText,
    renderPendingDecisionGlance,
    renderPromotionDiagnosticCard,
    renderTurnForm,
    lookupName,
    slugify
  });
  void syncPWABadge(state.pwaBadge, state);
}

// ── Nav rail ──

function renderNavRail() {
  const navCount = (id) => {
    if (id === "threads") return state.threads.length;
    if (id === "agents") return state.agents.length;
    if (id === "models") return state.inferenceRoutes.length;
    if (id === "workspaces") return state.workspaces.length;
    if (id === "turns") return state.turns.length;
    return 0;
  };

  const isActive = (id) => {
    return id === state.route.id ||
      (id === "turns" && state.route.id === "turn-detail") ||
      (id === "threads" && state.route.id === "thread-detail");
  };
  const renderNavItem = (item) => `
    <a class="nav-rail__item ${isActive(item.id) ? "nav-rail__item--active" : ""}" href="${item.href}" data-route data-tooltip="${escapeHTML(item.title)}">
      <span class="nav-rail__item-abbr">${escapeHTML(item.title[0])}</span>
      <span class="nav-rail__item-label">${escapeHTML(item.title)}</span>
      ${navCount(item.id) ? `<span class="nav-rail__count">${navCount(item.id)}</span>` : ""}
    </a>
  `;

  return `
    <nav class="nav-rail${state.navCollapsed ? " nav-rail--collapsed" : ""}">
      <div class="nav-rail__header">
        <div class="nav-rail__logo"><img src="/logo.png" alt="Lytebulb"></div>
        <span class="nav-rail__brand">Lytebulb</span>
        <button class="nav-rail__collapse" type="button" data-action="toggle-nav" title="${state.navCollapsed ? "Expand sidebar" : "Collapse sidebar"}">${state.navCollapsed ? "\u25B6" : "\u25C0"}</button>
      </div>
      <div class="nav-rail__items">
        ${NAV_ITEMS.map(renderNavItem).join("")}
        ${state.inferenceEnabled ? `<div class="nav-rail__sep"></div>${MODELS_NAV_ITEMS.map(renderNavItem).join("")}` : ""}
        <div class="nav-rail__sep"></div>
        ${DIAGNOSTIC_NAV_ITEMS.map(renderNavItem).join("")}
      </div>
    </nav>
  `;
}

// ── Breadcrumb bar ──

function renderBreadcrumbBar() {
  const routeID = state.route.id;
  const title = BREADCRUMB_TITLES[routeID] || "Threads";

  let breadcrumbHTML = `<span class="breadcrumb__current">${escapeHTML(title)}</span>`;
  let breadcrumbActions = "";

  if (routeID === "agents") {
    breadcrumbHTML = `<span class="breadcrumb__current">${escapeHTML(title)}</span><span class="breadcrumb__count">${state.agents.length}</span>`;
    const showForm = state.agentCreateOpen || Boolean(state.editingAgentID);
    breadcrumbActions = `<button class="button button--sm ${showForm ? "button--ghost" : "button--primary"}" type="button" data-action="toggle-agent-create">${showForm && !state.editingAgentID ? "Cancel" : state.editingAgentID ? "Cancel edit" : "+ New"}</button>`;
  } else if (routeID === "models") {
    breadcrumbHTML = `<span class="breadcrumb__current">${escapeHTML(title)}</span><span class="breadcrumb__count">${state.inferenceRoutes.length}</span>`;
  } else if (routeID === "workspaces") {
    breadcrumbHTML = `<span class="breadcrumb__current">${escapeHTML(title)}</span><span class="breadcrumb__count">${state.workspaces.length}</span>`;
    const showForm = state.workspaceCreateOpen || Boolean(state.editingWorkspaceID);
    breadcrumbActions = `<button class="button button--sm ${showForm ? "button--ghost" : "button--primary"}" type="button" data-action="toggle-workspace-create">${showForm && !state.editingWorkspaceID ? "Cancel" : state.editingWorkspaceID ? "Cancel edit" : "+ New"}</button>`;
  } else if (routeID === "thread-detail") {
    const thread = state.threadDetail.thread;
    const label = thread ? threadTitle(thread) : "Loading...";
    breadcrumbHTML = `
      <a href="/threads" data-route>Threads</a>
      <span class="breadcrumb__sep">/</span>
      <span class="breadcrumb__current">${escapeHTML(label)}</span>
    `;
  } else if (routeID === "turn-detail") {
    const turn = state.turnDetail.turn;
    const label = turn ? renderTruncatedIDText(turn.id) : "Loading...";
    breadcrumbHTML = `
      <a href="/turns" data-route>Diagnostics</a>
      <span class="breadcrumb__sep">/</span>
      <span class="breadcrumb__current">${escapeHTML(label)}</span>
    `;
  }

  return `
    <header class="breadcrumb-bar">
      <div class="breadcrumb-bar__left">
        <div class="breadcrumb">${breadcrumbHTML}</div>
        <span class="status-dot status-dot--${escapeHTML(state.status.tone)}" title="${escapeHTML(state.status.message)}"></span>
      </div>
      <div class="breadcrumb-bar__right">
        ${renderAccountScopeControl({ compact: true })}
        ${canPromptPWAInstall(state.pwaInstall) ? `<button class="button button--sm button--ghost" type="button" data-action="install-app">Install</button>` : ""}
        ${breadcrumbActions}
        <button class="button button--sm button--ghost" type="button" data-action="refresh">Refresh</button>
        <button class="settings-trigger" type="button" data-action="toggle-theme" title="Toggle light/dark">${document.documentElement.classList.contains("dark") ? "\u263E" : "\u2600"}</button>
        <button class="settings-trigger" type="button" data-action="toggle-settings" title="Settings">S</button>
      </div>
    </header>
  `;
}

// ── Settings popover ──

function renderSettingsPopover() {
  return `
    <div class="settings-popover">
      ${renderAuthForm({ mode: "settings" })}
      ${renderAccountScopeControl({ compact: false })}
      <div class="form-note" style="margin-top:var(--space-sm)">${escapeHTML(pwaInstallStatusText(state.pwaInstall))}</div>
    </div>
  `;
}

function renderAccountScopeControl(options = {}) {
  if (!state.apiKey) {
    return "";
  }
  const accountOptions = normalizedAccountOptions();
  const selectedValue = selectedAccountOptionValue(accountOptions);
  const selectedAccount = lookupByID(accountOptions, selectedValue);
  const summary = currentAccountSummary();
  const placement = options.compact ? "header" : "settings";
  const isOpen = state.accountSwitcherOpen && state.accountSwitcherPlacement === placement;
  const label = selectedAccount ? accountShortLabel(selectedAccount) : summary.label;
  const countLabel = accountOptions.length ? `${accountOptions.length} account${accountOptions.length === 1 ? "" : "s"}` : "Account scope";
  const switcherClass = `account-switcher ${options.compact ? "account-switcher--compact" : "account-switcher--settings"}`;
  const buttonTitle = accountOptions.length > 1 ? "Switch account" : "Inspect account";
  return `
    <div class="${switcherClass}" data-account-switcher="${escapeHTML(placement)}">
      <span class="account-switcher__eyebrow">Account</span>
      <button class="account-switcher__button" type="button" data-action="toggle-account-switcher" aria-expanded="${isOpen ? "true" : "false"}" title="${escapeHTML(buttonTitle)}">
        <span class="account-switcher__name">${escapeHTML(label)}</span>
        <span class="account-switcher__count">${escapeHTML(countLabel)}</span>
        <span class="account-switcher__chevron" aria-hidden="true">v</span>
      </button>
      ${isOpen ? renderAccountSwitcherPopover(accountOptions, selectedAccount || null) : ""}
    </div>
  `;
}

function renderAccountSwitcherPopover(accountOptions, selectedAccount) {
  const query = String(state.accountSwitcherQuery || "");
  const filtered = filterAccountOptions(accountOptions, query);
  const visible = filtered.slice(0, 50);
  const selectedValue = selectedAccountOptionValue(accountOptions);
  const previewAccount = lookupByID(accountOptions, state.accountSwitcherPreviewID);
  const detailsAccount = previewAccount || selectedAccount || visible[0] || null;
  const resultCount = query
    ? `${filtered.length} match${filtered.length === 1 ? "" : "es"}`
    : `${accountOptions.length} account${accountOptions.length === 1 ? "" : "s"}`;
  return `
    <div class="account-switcher__popover" role="dialog" aria-label="Account switcher">
      <div class="account-switcher__toolbar">
        <input class="account-switcher__search" type="search" name="account_search" value="${escapeHTML(query)}" placeholder="Search name, slug, id, metadata" autocomplete="off" autofocus>
        ${query ? `<button class="button button--sm button--ghost" type="button" data-action="clear-account-search">Clear</button>` : ""}
        <button class="button button--sm button--ghost" type="button" data-action="close-account-switcher">Close</button>
      </div>
      <div class="account-switcher__meta">${escapeHTML(resultCount)}${filtered.length > visible.length ? ` - showing first ${visible.length}` : ""}</div>
      <div class="account-switcher__content">
        <div class="account-switcher__list" role="listbox" aria-label="Accounts">
          ${visible.length ? visible.map((account) => accountSwitcherRowHTML(account, selectedValue, detailsAccount?.id || "")).join("") : renderAccountSwitcherEmpty()}
        </div>
        ${renderAccountDetailsPanel(detailsAccount, selectedValue)}
      </div>
    </div>
  `;
}

function accountSwitcherRowHTML(account, selectedValue, previewValue) {
  const id = String(account?.id || "").trim();
  const selected = id === selectedValue;
  const previewed = id === previewValue;
  const slug = String(account?.slug || "").trim();
  const stateLabel = String(account?.state || "active").trim();
  const metadata = account?.metadata && typeof account.metadata === "object" ? account.metadata : {};
  const scope = accountMetadataValue(metadata, "scope") || accountMetadataValue(metadata, "scope_kind");
  const owner = accountMetadataValue(metadata, "owner") || accountMetadataValue(metadata, "external_ref");
  return `
    <button class="account-switcher__row ${previewed ? "account-switcher__row--previewed" : ""} ${selected ? "account-switcher__row--selected" : ""}" type="button" data-account-preview="${escapeHTML(id)}" role="option" aria-selected="${previewed ? "true" : "false"}">
      <span class="account-switcher__row-main">
        <span class="account-switcher__row-title">${escapeHTML(accountShortLabel(account))}</span>
        <span class="account-switcher__row-sub">${escapeHTML(slug || id)}</span>
      </span>
      <span class="account-switcher__row-side">
        <span class="account-switcher__badge account-switcher__badge--${escapeHTML(accountStateTone(stateLabel))}">${escapeHTML(stateLabel)}</span>
        ${selected ? `<span class="account-switcher__badge account-switcher__badge--current">current</span>` : ""}
        ${account?.is_default ? `<span class="account-switcher__badge">default</span>` : ""}
        ${scope ? `<span class="account-switcher__badge">${escapeHTML(scope)}</span>` : ""}
        ${owner ? `<span class="account-switcher__row-id">${escapeHTML(renderTruncatedIDText(owner, 14))}</span>` : `<span class="account-switcher__row-id">${escapeHTML(renderTruncatedIDText(id, 14))}</span>`}
      </span>
    </button>
  `;
}

function renderAccountSwitcherEmpty() {
  return `
    <div class="account-switcher__empty">
      <strong>No matching accounts</strong>
      <span>Try a name, slug, id, or metadata value.</span>
    </div>
  `;
}

function renderAccountDetailsPanel(account, selectedValue) {
  if (!account) {
    return `
      <aside class="account-details-panel">
        <div class="account-details-panel__label">Account details</div>
        <div class="account-switcher__empty">
          <strong>No account loaded</strong>
          <span>Refresh the console to load account metadata.</span>
        </div>
      </aside>
    `;
  }
  const metadata = account?.metadata && typeof account.metadata === "object" ? account.metadata : {};
  const metadataEntries = Object.entries(metadata);
  const id = String(account?.id || "").trim();
  const isCurrent = id && id === selectedValue;
  return `
    <aside class="account-details-panel">
      <div class="account-details-panel__label">Account details</div>
      <div class="account-details-panel__title">${escapeHTML(accountShortLabel(account))}</div>
      <div class="account-details-panel__actions">
        ${isCurrent
          ? `<span class="account-switcher__badge account-switcher__badge--current">Current account</span>`
          : `<button class="button button--sm button--primary" type="button" data-account-switch="${escapeHTML(id)}">Use account</button>`}
      </div>
      <div class="account-details-panel__grid">
        ${accountDetailRowHTML("ID", account.id || "")}
        ${accountDetailRowHTML("Slug", account.slug || "")}
        ${accountDetailRowHTML("State", account.state || "active")}
        ${accountDetailRowHTML("Default", account.is_default ? "yes" : "no")}
        ${accountDetailRowHTML("Created", formatTimestamp(account.created_at))}
        ${accountDetailRowHTML("Updated", formatTimestamp(account.updated_at))}
      </div>
      <div class="account-details-panel__metadata">
        <div class="account-details-panel__label">Metadata</div>
        ${metadataEntries.length ? metadataEntries.map(([key, value]) => accountMetadataRowHTML(key, value)).join("") : `<span class="account-details-panel__muted">No metadata</span>`}
      </div>
    </aside>
  `;
}

function accountDetailRowHTML(label, value) {
  const isID = label === "ID";
  const text = String(value || "n/a");
  return `
    <div class="account-details-panel__row">
      <span title="${escapeHTML(label)}">${escapeHTML(label)}</span>
      <code title="${escapeHTML(text)}">${escapeHTML(isID ? renderTruncatedIDText(text, 22) : text)}</code>
    </div>
  `;
}

function accountMetadataRowHTML(key, value) {
  const text = accountMetadataValue({ value }, "value");
  return `
    <div class="account-details-panel__row account-details-panel__row--metadata">
      <span title="${escapeHTML(key)}">${escapeHTML(key)}</span>
      <code title="${escapeHTML(text)}">${escapeHTML(renderTruncatedIDText(text, 32))}</code>
    </div>
  `;
}

function filterAccountOptions(accounts, query) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  const selectedValue = selectedAccountOptionValue(accounts);
  const sorted = [...accounts].sort((left, right) => accountSortRank(left, selectedValue) - accountSortRank(right, selectedValue));
  if (!normalizedQuery) {
    return sorted;
  }
  return sorted.filter((account) => accountSearchText(account).includes(normalizedQuery));
}

function accountSortRank(account, selectedValue) {
  const id = String(account?.id || "").trim();
  if (id && id === selectedValue) {
    return 0;
  }
  if (account?.is_default) {
    return 1;
  }
  return 2;
}

function accountSearchText(account) {
  const metadata = account?.metadata && typeof account.metadata === "object" ? account.metadata : {};
  return [
    account?.id,
    account?.name,
    account?.slug,
    account?.state,
    JSON.stringify(metadata)
  ].filter(Boolean).join(" ").toLowerCase();
}

function accountShortLabel(account) {
  const name = String(account?.name || "").trim();
  const id = String(account?.id || "").trim();
  return name || id || "Default account";
}

function accountStateTone(stateLabel) {
  const normalized = String(stateLabel || "").toLowerCase();
  if (normalized === "active" || normalized === "enabled") {
    return "ok";
  }
  if (normalized === "disabled" || normalized === "deleted") {
    return "danger";
  }
  return "neutral";
}

function accountMetadataValue(metadata, key) {
  const value = metadata?.[key];
  if (value == null) {
    return "";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function normalizedAccountOptions() {
  if (!Array.isArray(state.accounts)) {
    return [];
  }
  const seen = new Set();
  const out = [];
  for (const account of state.accounts) {
    const id = String(account?.id || "").trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(account);
  }
  return out;
}

function selectedAccountOptionValue(accounts) {
  const selected = String(state.selectedAccountID || "").trim();
  if (selected) {
    return selected;
  }
  const defaultAccount = accounts.find((account) => Boolean(account?.is_default));
  return String(defaultAccount?.id || accounts[0]?.id || "").trim();
}

function currentAccountSummary() {
  const selected = String(state.selectedAccountID || "").trim();
  const selectedAccount = selected ? lookupByID(state.accounts, selected) : null;
  if (selectedAccount) {
    return { id: selected, label: accountDisplayName(selectedAccount) };
  }
  if (!selected && Array.isArray(state.accounts) && state.accounts.length === 1) {
    const account = state.accounts[0];
    return { id: account.id || "", label: accountDisplayName(account) };
  }
  if (selected) {
    return { id: selected, label: selected };
  }
  const inferred = inferCurrentAccountID();
  if (inferred) {
    return { id: inferred, label: inferred };
  }
  return { id: "", label: "Current account" };
}

function accountDisplayName(account) {
  const name = String(account?.name || "").trim();
  const id = String(account?.id || "").trim();
  if (name && id) {
    return `${name} (${id})`;
  }
  return name || id || "Default account";
}

function inferCurrentAccountID() {
  const collections = [state.agents, state.workspaces, state.threads, state.turns, state.inferenceCredentials, state.inferenceRoutes, state.inferenceTraffic];
  for (const collection of collections) {
    if (!Array.isArray(collection)) {
      continue;
    }
    for (const item of collection) {
      const accountID = String(item?.account_id || "").trim();
      if (accountID) {
        return accountID;
      }
    }
  }
  return "";
}

function renderAuthDialog() {
  return `
    <section class="auth-dialog" aria-labelledby="auth-dialog-title">
      <div class="auth-dialog__brand" aria-label="Lytebulb">
        <img class="auth-dialog__logo" src="/logo.png" alt="">
        <span class="auth-dialog__wordmark">Lytebulb</span>
      </div>
      <div>
        <h1 id="auth-dialog-title" class="auth-dialog__title">Runtime access</h1>
        <p class="auth-dialog__description">Enter a runtime API key to open the console.</p>
      </div>
      ${renderAuthForm({ mode: "dialog" })}
    </section>
  `;
}

function renderAuthForm(options = {}) {
  const sameOriginAuth = isSameOriginAuthToken(state.apiKey);
  const authStatus = state.authStatus || {};
  const busy = authStatus.tone === "busy";
  const buttonLabel = busy ? "Checking" : (state.apiKey ? "Save" : "Unlock");
  return `
    <form id="auth-form" class="auth-form">
      <div class="field">
        <label for="api-key-input">API Key</label>
        <input id="api-key-input" name="api_key" type="password" placeholder="${sameOriginAuth ? "Same-origin auth enabled" : "lyteb_..."}" value="${escapeHTML(state.authInput)}" autocomplete="off" ${busy ? "disabled" : ""}>
      </div>
      ${sameOriginAuth ? `<div class="form-note">Using same-origin auth. Enter a bearer key only to override it.</div>` : ""}
      ${renderAuthFeedback(authStatus)}
      <div class="auth-actions">
        <button class="button button--primary button--sm" type="submit" ${busy ? "disabled" : ""}>${buttonLabel}</button>
        ${options.mode === "settings" ? `<button class="button button--ghost button--sm" type="button" data-action="close-settings">Close</button>` : ""}
      </div>
    </form>
  `;
}

function renderAuthFeedback(authStatus) {
  const message = String(authStatus?.message || "").trim();
  if (!message) {
    return "";
  }
  const tone = ["ok", "busy", "error"].includes(authStatus?.tone) ? authStatus.tone : "idle";
  const role = tone === "error" ? "alert" : "status";
  return `<div class="auth-feedback auth-feedback--${escapeHTML(tone)}" role="${role}">${escapeHTML(message)}</div>`;
}

// ── Page router ──

function renderPage(routeID = state.route.id) {
  if (!state.apiKey) return renderLockedState();
  switch (routeID) {
  case "thread-detail": return renderThreadDetailPage();
  case "turn-detail": return renderTurnDetailComponent(state.turnDetail, {
    agents: state.agents,
    workspaces: state.workspaces,
    decisionDrafts: state.decisionDrafts,
    lookupTurn: lookupDecisionTurn,
    workspaceDiagnostics: state.turnDetail.workspaceDiagnostics,
    turnDiagnostics: state.turnDetail.turnDiagnostics
  });
  default: return renderLockedState();
  }
}

// ── Locked state ──

function renderLockedState() {
  return `
    <div class="empty-state">
      <div class="empty-state__icon">L</div>
      <div class="empty-state__title">Unlock the runtime console</div>
      <div class="empty-state__description">Enter a valid API key using the settings button in the top right to load agents, workspaces, threads, and turns.</div>
    </div>
  `;
}

// ── Helpers ──

function renderMetrics(items) {
  return `
    <section class="metrics">
      ${items.map((item) => `
        <article class="metric-card">
          <div class="metric-label">${escapeHTML(item.label)}</div>
          <div class="metric-value">${escapeHTML(item.value)}</div>
          <div class="metric-meta">${escapeHTML(item.meta)}</div>
        </article>
      `).join("")}
    </section>
  `;
}

// ── Thread detail page ──

function renderThreadDetailPage() {
  const current = state.threadDetail.thread;
  if (current?.mode === "llm") {
    const toolbarActionsHTML = current ? `<button class="button button--sm button--ghost" type="button" data-action="toggle-detail-drawer">Details</button>` : "";
    return renderThreadComponent(state.threadDetail, {
      agentLabel: current ? lookupName(state.agents, current.agent_id) : "",
      workspaceLabel: "Inference backend",
      inferenceRoutes: state.inferenceRoutes,
      toolbarActionsHTML,
      afterToolbarHTML: "",
      notFoundActionHTML: `<div class="button-row"><a class="button button--primary" href="/threads" data-route>Back to threads</a></div>`
    });
  }
  const workspaceItem = current ? lookupByID(state.workspaces, current.workspace_id) : null;
  const threadPromotions = current ? promotionsForThread(current.id) : [];
  const latestPromotion = threadPromotions[0] || current?.latest_promotion || null;
  const targetBranch = latestPromotion?.target_branch || workspaceItem?.default_branch || current?.last_base_ref || "main";
  const toolbarActionsHTML = current ? `
    ${workspaceItem?.git_state === "ready" ? `<button class="button button--sm button--ghost${state.threadContentPanelOpen ? " button--active" : ""}" type="button" data-action="toggle-thread-content-panel">Files</button>` : ""}
    <button class="button button--sm button--ghost" type="button" data-action="toggle-detail-drawer">Details</button>
  ` : "";

  return renderThreadComponent(state.threadDetail, {
    agentLabel: current ? lookupName(state.agents, current.agent_id) : "",
    workspaceLabel: current ? lookupName(state.workspaces, current.workspace_id) : "",
    metaSuffix: current ? threadMergeStateSuffix(current, targetBranch) : "",
    toolbarActionsHTML,
    afterToolbarHTML: current ? renderPromotionBar(current, workspaceItem, threadPromotions) : "",
    notFoundActionHTML: `<div class="button-row"><a class="button button--primary" href="/threads" data-route>Back to threads</a></div>`
  });
}

function renderThreadContentPanel() {
  const current = state.threadDetail.thread;
  if (current?.mode === "llm") return "";
  if (!current) return "";
  const workspaceItem = lookupByID(state.workspaces, current.workspace_id);
  if (!workspaceItem) return "";
  return `
    <aside class="thread-content-panel" data-workspace-content-panel>
      <div class="thread-content-panel__header">
        <span class="mini-label">Workspace files</span>
        <button class="button button--sm button--ghost" type="button"
                data-action="toggle-thread-content-panel">&times;</button>
      </div>
      ${renderWorkspaceContentPanel(state.workspaceContent, workspaceItem, { showSnapshotReplace: false })}
    </aside>
  `;
}

function renderPromotionBar(threadItem, workspaceItem, promotions) {
  const latest = promotions[0] || threadItem.latest_promotion || null;
  const targetBranch = latest?.target_branch || workspaceItem?.default_branch || threadItem.last_base_ref || "main";
  const isConflicted = threadItem.state === "merge_conflicted";
  const isMerging = threadItem.state === "merging" || threadItem.state === "awaiting_remote_merge";
  const isArchived = threadItem.state === "archived" || threadItem.state === "merged";
  const options = Array.isArray(threadItem.promotion_mode_options) ? threadItem.promotion_mode_options : [];
  const canLocalMerge = options.includes("local_merge");
  const canRemotePush = options.includes("remote_push");
  const canRemotePR = options.includes("remote_pr");

  if (isConflicted) {
    const hint = latest?.conflict_summary || "Merge conflicted. Resolve conflicts in the draft branch, then retry.";
    const retryMode = canRemotePush ? "remote_push" : "local_merge";
    return `<div class="thread-recovery-banner">${escapeHTML(hint)} <button class="button button--sm button--primary" type="button" data-action="merge-into-target" data-promotion-mode="${escapeHTML(retryMode)}" data-target-branch="${escapeHTML(targetBranch)}" style="margin-left:var(--space-sm)">Retry merge</button></div>`;
  }

  if (threadItem.state === "recovery_required" && latest?.mode === "remote_push" && latest?.id) {
    return `
      <div class="thread-recovery-banner">
        ${escapeHTML(latest.conflict_summary || "Remote push failed after the canonical target branch was updated locally.")}
        <button class="button button--sm button--primary" type="button" data-action="retry-promotion" data-promotion-id="${escapeHTML(latest.id)}" style="margin-left:var(--space-sm)">Retry push</button>
      </div>
    `;
  }

  if (threadItem.state === "awaiting_remote_merge" && latest?.mode === "remote_pr") {
    return `
      <div class="promotion-bar promotion-bar--busy">
        Waiting for remote merge into ${escapeHTML(targetBranch)}.
        ${latest.remote_review_url ? `<a class="button button--sm button--ghost" href="${escapeHTML(latest.remote_review_url)}" target="_blank" rel="noreferrer">Open merge request</a>` : ""}
        ${latest.id ? `<button class="button button--sm button--ghost" type="button" data-action="cancel-promotion" data-promotion-id="${escapeHTML(latest.id)}">Cancel</button>` : ""}
      </div>
    `;
  }

  if (isMerging) {
    const label = latest?.mode === "remote_pr"
      ? `Preparing merge request for ${targetBranch}\u2026`
      : latest?.mode === "remote_push"
        ? `Merging and pushing into ${targetBranch}\u2026`
        : `Merging into ${targetBranch}\u2026`;
    return `<div class="promotion-bar promotion-bar--busy">${escapeHTML(label)}</div>`;
  }

  if (isArchived) return "";

  if (threadItem.mergeable) {
    if (state.promotionConfirm && state.promotionConfirm.threadID === threadItem.id) {
      const pc = state.promotionConfirm;
      const modeLabel = promotionModeLabel(pc.mode);
      const confirmLabel = pc.mode === "remote_pr"
        ? "Open merge request"
        : pc.mode === "remote_push"
          ? `Merge and push ${escapeHTML(pc.targetBranch)}`
          : `Merge into ${escapeHTML(pc.targetBranch)}`;
      return `
        <div class="promotion-bar" style="flex-direction:column;align-items:stretch;gap:var(--space-sm)">
          <div style="font-weight:600">${escapeHTML(modeLabel)} into ${escapeHTML(pc.targetBranch)}</div>
          <div style="font-size:var(--font-sm);color:var(--color-text-muted)">
            Draft branch: ${escapeHTML(threadItem.draft_branch || "–")} · Head: ${escapeHTML((threadItem.last_head_commit || "").slice(0, 8) || "–")}
          </div>
          <div class="field"><textarea id="promotion-description" placeholder="Optional description..." rows="2">${escapeHTML(pc.description)}</textarea></div>
          <div style="display:flex;gap:var(--space-sm);justify-content:flex-end">
            <button class="button button--sm button--ghost" type="button" data-action="cancel-promotion-confirm">Cancel</button>
            <button class="button button--sm button--primary" type="button" data-action="submit-promotion-confirm">${confirmLabel}</button>
          </div>
        </div>
      `;
    }
    const actions = [];
    if (canLocalMerge) {
      actions.push(`<button class="button button--sm button--primary" type="button" data-action="merge-into-target" data-promotion-mode="local_merge" data-target-branch="${escapeHTML(targetBranch)}">Merge into ${escapeHTML(targetBranch)}</button>`);
    }
    if (canRemotePush) {
      actions.push(`<button class="button button--sm button--primary" type="button" data-action="merge-into-target" data-promotion-mode="remote_push" data-target-branch="${escapeHTML(targetBranch)}">Merge and push ${escapeHTML(targetBranch)}</button>`);
    }
    if (canRemotePR) {
      actions.push(`<button class="button button--sm button--ghost" type="button" data-action="merge-into-target" data-promotion-mode="remote_pr" data-target-branch="${escapeHTML(targetBranch)}">Open merge request</button>`);
    }
    return `
      <div class="promotion-bar">
        <span class="promotion-bar__text">Ready to promote into <strong>${escapeHTML(targetBranch)}</strong></span>
        <div class="promotion-bar__actions">${actions.join("")}</div>
      </div>
    `;
  }

  return "";
}

function renderPromotionDiagnosticCard(item) {
  const threadItem = lookupByID(state.threads, item.thread_id);
  return `
    <article class="promotion-diagnostic-card">
      <div>
        <div class="promotion-diagnostic-card__title">${escapeHTML(threadItem ? threadTitle(threadItem) : item.thread_id || "Unknown thread")}</div>
        <div class="promotion-diagnostic-card__meta">${escapeHTML(promotionModeLabel(item.mode))} → ${escapeHTML(item.target_branch || "main")} · ${escapeHTML(formatTimestamp(item.created_at))}</div>
        ${item.conflict_summary ? `<div class="promotion-diagnostic-card__warn">${escapeHTML(item.conflict_summary)}</div>` : ""}
      </div>
      <div class="promotion-diagnostic-card__side">
        <span class="badge badge--${escapeHTML(promotionBadgeTone(item))}">${escapeHTML(promotionStatusLabel(item))}</span>
        ${threadItem ? `<a class="button button--sm button--ghost" href="/threads/${encodeURIComponent(threadItem.id)}" data-route>Open thread</a>` : ""}
      </div>
    </article>
  `;
}

// ── Detail drawer (thread context) ──

function renderDetailDrawer() {
  const current = state.threadDetail.thread;
  const turnItem = state.turnDetail.turn;

  if (state.route.id === "thread-detail" && current) {
    if (current.mode === "llm") {
      return renderLLMThreadDetailDrawer(state.threadDetail, {
        renderContextRow,
        lookupAgentName: (agentID) => lookupName(state.agents, agentID),
        renderTurns: (turns) => renderThreadTurnsForDrawer(turns)
      });
    }
    return renderHarnessThreadDetailDrawer(state.threadDetail, {
      renderContextRow,
      lookupAgentName: (agentID) => lookupName(state.agents, agentID),
      lookupWorkspaceName: (workspaceID) => lookupName(state.workspaces, workspaceID),
      renderTurns: (turns) => renderThreadTurnsForDrawer(turns)
    });
  }

  if (state.route.id === "turn-detail" && turnItem) {
    return renderTurnDetailDrawer(state.turnDetail, {
      workspaceDiagnostics: state.turnDetail.workspaceDiagnostics,
      turnDiagnostics: state.turnDetail.turnDiagnostics
    });
  }

  return "";
}

function renderThreadTurnsForDrawer(turns) {
  if (!Array.isArray(turns) || turns.length === 0) {
    return `<div class="empty">No turns yet.</div>`;
  }
  return [...turns].reverse().map((item) => renderAdminTurnRow(state, item, true, { lookupName })).join("");
}

function renderTurnForm() {
  const turnDraft = state.forms.turn;
  const harnessAgents = state.agents.filter((item) => String(item.execution_mode || "harness") === "harness");
  return `
    <form id="turn-form" class="form-grid">
      <div class="form-grid form-grid--split">
        <div class="field">
          <label for="turn-agent-id">Agent</label>
          <select id="turn-agent-id" name="agent_id" required>
            <option value="">Select agent</option>
            ${harnessAgents.map((item) => `<option value="${escapeHTML(item.id)}" ${turnDraft.agent_id === item.id ? "selected" : ""}>${escapeHTML(item.name)}</option>`).join("")}
          </select>
        </div>
        <div class="field">
          <label for="turn-workspace-id">Workspace</label>
          <select id="turn-workspace-id" name="workspace_id" required>
            <option value="">Select workspace</option>
            ${state.workspaces.map((item) => `<option value="${escapeHTML(item.id)}" ${turnDraft.workspace_id === item.id ? "selected" : ""}>${escapeHTML(item.name)}</option>`).join("")}
          </select>
        </div>
      </div>
      <div class="field">
        <label for="turn-prompt">Prompt</label>
        <textarea id="turn-prompt" name="prompt" required placeholder="Describe what to do...">${escapeHTML(turnDraft.prompt)}</textarea>
      </div>
      <div class="form-grid form-grid--split">
        <div class="field">
          <label for="turn-base-ref">Base ref</label>
          <input id="turn-base-ref" name="base_ref" placeholder="main" value="${escapeHTML(turnDraft.base_ref)}">
        </div>
        <div class="field">
          <label for="turn-branch-name">Branch name</label>
          <input id="turn-branch-name" name="branch_name" placeholder="feature/..." value="${escapeHTML(turnDraft.branch_name)}">
        </div>
      </div>
      ${state.formErrors.turn ? `<div class="form-error">${escapeHTML(state.formErrors.turn)}</div>` : ""}
      <div class="button-row">
        <button class="button button--primary" type="submit">Queue turn</button>
      </div>
    </form>
  `;
}

function renderPendingDecisionGlance(item) {
  const href = item.thread_id ? `/threads/${encodeURIComponent(item.thread_id)}` : `/turns/${encodeURIComponent(item.turn_id)}`;
  const linkLabel = item.thread_id ? "Open thread" : "Open turn";
  return `
    <a class="decision-glance" href="${href}" data-route>
      <div class="decision-glance__main">
        <div>
          <div class="decision-glance__title">${escapeHTML(item.summary || item.prompt || "Decision required")}</div>
          <div class="decision-glance__meta">${escapeHTML(lookupName(state.agents, item.agent_id))} → ${escapeHTML(lookupName(state.workspaces, item.workspace_id))}</div>
          <div class="decision-glance__prompt">${escapeHTML(compactPrompt(item.prompt || item.detail || ""))}</div>
        </div>
        <div class="decision-glance__side">
          <span class="badge badge--decision-pending">${escapeHTML(decisionKindLabel(item.kind))}</span>
          <span class="decision-glance__link">${escapeHTML(linkLabel)}</span>
        </div>
      </div>
    </a>
  `;
}

// ── Domain helpers (unchanged) ──

function lookupName(items, id) { const match = items.find((item) => item.id === id); return match ? match.name : id || "unknown"; }
function lookupByID(items, id) { return items.find((item) => item.id === id) || null; }
function lookupDecision(id) {
  if (state.turnDetail.decisions.some((item) => item.id === id)) return state.turnDetail.decisions.find((item) => item.id === id) || null;
  for (const items of Object.values(state.threadDetail.decisionsByTurnID || {})) {
    const match = items.find((item) => item.id === id);
    if (match) return match;
  }
  return state.pendingDecisions.find((item) => item.id === id) || null;
}

function agentDraftFromItem(item) {
  const executionMode = String(item.execution_mode || "harness");
  const draft = {
    ...defaultAgentDraft(),
    name: String(item.name || ""),
    execution_mode: executionMode,
    adapter_id: String(item.adapter_id || "opencode.server"),
    model: executionMode === "llm" ? "" : String(item.model || ""),
    system_prompt: String(item.system_prompt || ""),
    credential_ref: String(item.credential_ref || ""),
    harness_id: "",
    runtime_config: ""
  };
  const rawRuntimeConfig = String(item.runtime_config_json || "").trim();
  let runtimeConfigObject = null;
  if (rawRuntimeConfig && rawRuntimeConfig !== "{}") {
    try { const parsed = JSON.parse(rawRuntimeConfig); if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) { runtimeConfigObject = { ...parsed }; } else { draft.runtime_config = rawRuntimeConfig; } } catch (_error) { draft.runtime_config = rawRuntimeConfig; }
  }
  if (runtimeConfigObject) {
    const capabilityDefaults = normalizeAgentCapabilityDefaults(runtimeConfigObject.capability_defaults);
    if (Object.values(capabilityDefaults).some((item) => item.route_id || item.model)) {
      draft.capability_defaults = capabilityDefaults;
      delete runtimeConfigObject.capability_defaults;
    }
    if (!draft.capability_defaults.responses.route_id) draft.capability_defaults.responses.route_id = typeof runtimeConfigObject.response_route_id === "string" ? runtimeConfigObject.response_route_id.trim() : "";
    if (!draft.capability_defaults.audio_transcription.route_id) draft.capability_defaults.audio_transcription.route_id = typeof runtimeConfigObject.audio_transcription_route_id === "string" ? runtimeConfigObject.audio_transcription_route_id.trim() : "";
    if (!draft.capability_defaults.audio_translation.route_id) draft.capability_defaults.audio_translation.route_id = typeof runtimeConfigObject.audio_translation_route_id === "string" ? runtimeConfigObject.audio_translation_route_id.trim() : "";
    if (!draft.capability_defaults.audio_speech.route_id) draft.capability_defaults.audio_speech.route_id = typeof runtimeConfigObject.audio_speech_route_id === "string" ? runtimeConfigObject.audio_speech_route_id.trim() : "";
    if (!draft.capability_defaults.image_generation.route_id) draft.capability_defaults.image_generation.route_id = typeof runtimeConfigObject.image_generation_route_id === "string" ? runtimeConfigObject.image_generation_route_id.trim() : "";
    if (draft.execution_mode === "llm" && !draft.capability_defaults.responses.model) draft.capability_defaults.responses.model = String(item.model || "").trim();
    delete runtimeConfigObject.response_route_id;
    delete runtimeConfigObject.audio_transcription_route_id;
    delete runtimeConfigObject.audio_translation_route_id;
    delete runtimeConfigObject.audio_speech_route_id;
    delete runtimeConfigObject.image_generation_route_id;
    if (draft.adapter_id === "opencode.server") {
      const serverURL = typeof runtimeConfigObject.server_url === "string" ? runtimeConfigObject.server_url.trim() : "";
      if (serverURL) { const matchingHarness = harnessesForAdapter(draft.adapter_id).find((current) => current.mode === "attached" && String(current.server_url || "").trim() === serverURL); if (matchingHarness) { draft.harness_id = matchingHarness.id; delete runtimeConfigObject.server_url; } }
    }
    draft.runtime_config = Object.keys(runtimeConfigObject).length > 0 ? JSON.stringify(runtimeConfigObject, null, 2) : "";
  }
  if (draft.execution_mode === "harness" && draft.adapter_id === "opencode.server" && !draft.harness_id) { const avail = harnessesForAdapter(draft.adapter_id); draft.harness_id = avail[0]?.id || ""; }
  return normalizeAgentDraft(draft);
}

function workspaceDraftFromItem(item, diagnostics = null) {
  return {
    ...defaultWorkspaceDraft(),
    name: String(item.name || ""),
    repo_source_kind: String(item.repo_source_kind || "remote"),
    default_branch: String(item.default_branch || ""),
    authoring_root_path: String(diagnostics?.authoring_root_path || ""),
    remote_name: String(item.remote_name || "origin"),
    remote_url: String(item.remote_url || ""),
    fetch_auth_ref: String(diagnostics?.fetch_auth_ref || ""),
    push_auth_ref: String(diagnostics?.push_auth_ref || ""),
    review_provider: String(item.review_provider || ""),
    review_auth_ref: String(diagnostics?.review_auth_ref || "")
  };
}
function uniqueCount(items) { return new Set(items.filter(Boolean)).size; }
function compactPrompt(value) { const trimmed = String(value || "").trim(); if (trimmed.length <= 80) return trimmed || "No prompt"; return `${trimmed.slice(0, 77)}...`; }
function truncatePath(value) { const str = String(value || ""); if (str.length <= 50) return str; const parts = str.split("/").filter(Boolean); if (parts.length <= 3) return str; return `.../${parts.slice(-3).join("/")}`; }
function hasActiveWork() {
  return state.turns.some((item) => canCancel(item.state)) ||
    state.threads.some((item) => item.state === "running" || item.state === "merging" || item.state === "awaiting_remote_merge");
}
function setStatus(message, tone) { setSurfaceStatus(state, message, tone); }
function requireAPIKey() { if (!state.apiKey) throw new Error("Enter an API key first."); }

function defaultThreadDraft() { return { agent_id: "", workspace_id: "", base_ref: "" }; }
function defaultAgentCapabilityDefaults() {
  return {
    responses: { route_id: "", model: "" },
    audio_transcription: { route_id: "", model: "" },
    audio_translation: { route_id: "", model: "" },
    audio_speech: { route_id: "", model: "" },
    image_generation: { route_id: "", model: "" }
  };
}
function normalizeAgentCapabilityDefaults(input = null) {
  const next = defaultAgentCapabilityDefaults();
  const source = input && typeof input === "object" ? input : {};
  for (const capability of Object.keys(next)) {
    const current = source[capability];
    if (!current || typeof current !== "object" || Array.isArray(current)) continue;
    next[capability] = {
      route_id: String(current.route_id || "").trim(),
      model: String(current.model || "").trim()
    };
  }
  return next;
}
function agentCapabilityFieldName(capability, field) { return `capability_defaults.${capability}.${field}`; }
function applyFlatAgentCapabilityDefaultFields(next) {
  const capabilityDefaults = normalizeAgentCapabilityDefaults(next.capability_defaults);
  for (const capability of Object.keys(capabilityDefaults)) {
    for (const field of ["route_id", "model"]) {
      const flatKey = agentCapabilityFieldName(capability, field);
      if (Object.prototype.hasOwnProperty.call(next, flatKey)) {
        capabilityDefaults[capability][field] = String(next[flatKey] || "").trim();
        delete next[flatKey];
      }
    }
  }
  next.capability_defaults = capabilityDefaults;
}
function defaultAgentDraft() { return { name: "", execution_mode: "harness", adapter_id: "opencode.server", harness_id: "", model: "gpt-5-nano", system_prompt: "", credential_ref: "", runtime_config: "", capability_defaults: defaultAgentCapabilityDefaults() }; }
function syncAgentDraftDefaults() { state.forms.agent = normalizeAgentDraft(state.forms.agent); }
function normalizeAgentDraft(draft) {
  const next = { ...defaultAgentDraft(), ...draft };
  applyFlatAgentCapabilityDefaultFields(next);
  next.execution_mode = next.execution_mode === "llm" ? "llm" : "harness";
  next.name = String(next.name || "");
  next.model = String(next.model || "").trim();
  next.system_prompt = String(next.system_prompt || "");
  next.credential_ref = String(next.credential_ref || "").trim();
  next.runtime_config = String(next.runtime_config || "");
  if (next.execution_mode === "llm") {
    next.adapter_id = "";
    next.harness_id = "";
    next.model = "";
    return next;
  }
  if (!next.adapter_id) next.adapter_id = "opencode.server";
  const avail = harnessesForAdapter(next.adapter_id);
  if (next.adapter_id === "opencode.server") {
    if (!avail.some((item) => item.id === next.harness_id)) next.harness_id = avail[0]?.id || "";
  } else {
    next.harness_id = "";
  }
  return next;
}
function normalizeThreadDraft(draft) {
  const next = { ...defaultThreadDraft(), ...draft };
  next.agent_id = String(next.agent_id || "").trim();
  next.workspace_id = String(next.workspace_id || "").trim();
  next.base_ref = String(next.base_ref || "").trim();
  const selectedAgent = lookupByID(state.agents, next.agent_id);
  if (String(selectedAgent?.execution_mode || "harness") === "llm") {
    next.workspace_id = "";
    next.base_ref = "";
  }
  return next;
}
function defaultInferenceCredentialDraft() { return { provider: "openai", name: "", credential_ref: "", base_url: "", scopes_csv: "", enabled: true }; }
function defaultInferenceRouteDraft() { return { name: "", provider_client_id: "openai_compatible", credential_id: "", capability_family: "responses", match_mode: "", match_value: "", advertised_models_csv: "", base_url_override: "", default_model: "", timeout_ms: 60000, max_retries: 0, enabled: true, is_default: false }; }
function defaultInferencePlaygroundDraft() {
  return {
    route_id: "",
    model: "",
    prompt: "",
    system_prompt: "",
    temperature: "",
    max_tokens: "",
    top_p: "",
    stream: false,
    size: "",
    quality: "",
    n: "",
    response_format: "",
    voice: "",
    speed: "",
    language: "",
    dimensions: "",
    encoding_format: "",
    file: ""
  };
}
function slugify(s) { return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
function defaultWorkspaceDraft() {
  return {
    name: "",
    repo_source_kind: "remote",
    default_branch: "main",
    authoring_root_path: "",
    remote_name: "origin",
    remote_url: "",
    fetch_auth_ref: "",
    push_auth_ref: "",
    review_provider: "",
    review_auth_ref: ""
  };
}
function defaultTurnDraft() { return { agent_id: "", workspace_id: "", prompt: "", base_ref: "", branch_name: "" }; }
function defaultPromotionDraft(threadItem, workspaceItem) { return { target_branch: String(workspaceItem?.default_branch || threadItem?.last_base_ref || "main"), mode: "local_merge" }; }
function formKeyForID(formID) { switch (formID) { case "agent-form": return "agent"; case "workspace-form": return "workspace"; case "thread-form": return "thread"; case "turn-form": return "turn"; case "inference-credential-form": return "inferenceCredential"; case "inference-route-form": return "inferenceRoute"; case "inference-playground-form": return "inferencePlayground"; default: return ""; } }
function captureDraftFromForm(form, formKey) {
  state.forms[formKey] = captureFormDraft(form, state.forms[formKey]);
}
function parseRuntimeConfig(raw) { const trimmed = String(raw || "").trim(); if (!trimmed) return { value: null, error: "" }; try { const value = JSON.parse(trimmed); if (value === null || Array.isArray(value) || typeof value !== "object") return { value: null, error: "runtime_config must be a JSON object" }; return { value, error: "" }; } catch (_error) { return { value: null, error: "runtime_config must be valid JSON" }; } }
function harnessesForAdapter(adapterID) { return state.harnesses.filter((item) => item.adapter_id === adapterID); }

function lookupHarnessForDraft(draft) { return harnessesForAdapter(draft.adapter_id).find((item) => item.id === draft.harness_id) || null; }
function harnessModeLabel(harness) { return harness?.mode === "attached" ? "External server" : "Built-in"; }
function harnessDisplayLabel(harness) { if (!harness) return "Built-in OpenCode"; return `${harness.label} (${harnessModeLabel(harness)})`; }
function buildAgentRuntimeConfig(draft, overrides) {
  const config = {};
  if (overrides && typeof overrides === "object") {
    for (const [key, value] of Object.entries(overrides)) config[key] = value;
  }
  delete config.response_route_id;
  delete config.audio_transcription_route_id;
  delete config.audio_translation_route_id;
  delete config.audio_speech_route_id;
  delete config.image_generation_route_id;
  if (draft.execution_mode !== "harness") {
    delete config.server_url;
    const capabilityDefaults = normalizeAgentCapabilityDefaults(draft.capability_defaults);
    const compactDefaults = {};
    for (const [capability, item] of Object.entries(capabilityDefaults)) {
      const routeID = String(item.route_id || "").trim();
      const model = String(item.model || "").trim();
      if (!routeID && !model) continue;
      compactDefaults[capability] = {};
      if (routeID) compactDefaults[capability].route_id = routeID;
      if (model) compactDefaults[capability].model = model;
    }
    if (Object.keys(compactDefaults).length > 0) config.capability_defaults = compactDefaults; else delete config.capability_defaults;
    return Object.keys(config).length > 0 ? config : null;
  }
  delete config.capability_defaults;
  const harness = lookupHarnessForDraft(draft);
  if (draft.adapter_id === "opencode.server") {
    delete config.server_url;
    if (harness?.mode === "attached" && harness.server_url) config.server_url = harness.server_url;
  }
  return Object.keys(config).length > 0 ? config : null;
}
function staleAttachedServerURL(draft) { if (draft.execution_mode !== "harness" || draft.adapter_id !== "opencode.server") return ""; const parsed = parseRuntimeConfig(draft.runtime_config); if (parsed.error || !parsed.value) return ""; const serverURL = typeof parsed.value.server_url === "string" ? parsed.value.server_url.trim() : ""; if (!serverURL) return ""; const harness = lookupHarnessForDraft(draft); if (harness?.mode === "attached" && String(harness.server_url || "").trim() === serverURL) return ""; return serverURL; }
function validateAgentDraft(draft, runtimeConfig) { if (!draft.name.trim()) return "name is required"; if (draft.execution_mode === "llm") return ""; if (draft.adapter_id !== "opencode.server") return ""; const harness = lookupHarnessForDraft(draft); if (!harness) return "no configured OpenCode harnesses; configure LYTEBULB_HARNESSES_JSON or use noop"; if (harness.mode === "attached" && !runtimeConfig?.server_url) return "attached harness missing server_url"; return ""; }
function validateThreadDraft(draft) { if (!draft.agent_id) return "agent_id is required"; const selectedAgent = lookupByID(state.agents, draft.agent_id); if (!selectedAgent) return "select a valid agent"; if (selectedAgent.execution_mode === "llm") { if (String(draft.workspace_id || "").trim()) return "workspace_id must be empty for llm threads"; return ""; } if (!draft.workspace_id) return "workspace_id is required"; return ""; }
function inferenceCredentialDraftFromItem(item) { return { ...defaultInferenceCredentialDraft(), provider: String(item.provider || "openai"), name: String(item.name || ""), credential_ref: String(item.credential_ref || ""), base_url: String(item.base_url || ""), scopes_csv: Array.isArray(item.scopes) ? item.scopes.join(",") : "", enabled: Boolean(item.enabled) }; }
function inferenceRouteDraftFromItem(item) { return { ...defaultInferenceRouteDraft(), name: String(item.name || ""), provider_client_id: String(item.provider_client_id || "openai_compatible"), credential_id: String(item.credential_id || ""), capability_family: String(item.capability_family || "responses"), match_mode: String(item.match_mode || ""), match_value: String(item.match_value || ""), advertised_models_csv: Array.isArray(item.advertised_models) ? item.advertised_models.join(",") : "", base_url_override: String(item.base_url_override || ""), default_model: String(item.default_model || ""), timeout_ms: Number(item.timeout_ms || 60000), max_retries: Number(item.max_retries || 0), enabled: Boolean(item.enabled), is_default: Boolean(item.is_default) }; }
function validateInferenceCredentialDraft(draft, options = {}) { if (!String(draft.name || "").trim()) return "name is required"; if (!String(draft.credential_ref || "").trim()) return "credential_ref is required"; if (!options.editing && !String(draft.secret || "").trim()) return "secret is required"; return ""; }
function validateInferenceRouteDraft(draft) { if (!String(draft.name || "").trim()) return "name is required"; if (!String(draft.provider_client_id || "").trim()) return "provider_client_id is required"; if (!String(draft.credential_id || "").trim()) return "credential_id is required"; if (!String(draft.capability_family || "").trim()) return "capability_family is required"; if (!draft.is_default && !String(draft.match_mode || "").trim()) return "match_mode is required unless default route is enabled"; if ((draft.match_mode === "exact_model" || draft.match_mode === "prefix_model") && !String(draft.match_value || "").trim()) return "match_value is required for the selected match_mode"; return ""; }
function validateWorkspaceDraft(draft, options = {}) {
  if (!String(draft.name || "").trim()) return "name is required";
  const editing = Boolean(options.editing);
  const repoSourceKind = String(draft.repo_source_kind || "remote").trim() || "remote";
  const remoteURL = String(draft.remote_url || "").trim();
  const authoringRootPath = String(draft.authoring_root_path || "").trim();
  if (!editing && repoSourceKind === "remote" && !remoteURL) return "remote_url is required";
  if ((editing || repoSourceKind === "local_authoring") && authoringRootPath) {
    if (authoringRootPath.startsWith("\\") || /^[A-Za-z]:[\\/]/.test(authoringRootPath) || authoringRootPath.startsWith("/")) return "";
    return "authoring_root_path must be absolute";
  }
  if (repoSourceKind === "local_authoring" && !editing && !authoringRootPath) return "authoring_root_path is required";
  return "";
}

function workspacePrimaryLocation(item) {
  if (item.repo_source_kind === "local_authoring") {
    return item.authoring_root_path ? `local source: ${item.authoring_root_path}` : "local source folder";
  }
  return item.remote_url || "remote repo";
}

function workspaceStateSummary(item) {
  const parts = [];
  if (item.repo_source_kind === "local_authoring") {
    parts.push("managed repo from local source");
  } else {
    parts.push("remote repo");
  }
  parts.push(String(item.remote_connection_state || "disconnected"));
  parts.push(String(item.default_branch || "main"));
  if (item.review_provider) parts.push(`review:${item.review_provider}`);
  return parts.join(" · ");
}

function workspacePrimaryAction(item) {
  switch (String(item.git_state || "registered")) {
    case "ready":
      return { action: "sync-workspace", label: item.repo_source_kind === "local_authoring" ? "Sync source" : "Sync" };
    case "sync_failed":
    case "recovery_required":
      return { action: "recover-workspace", label: "Recover" };
    default:
      return { action: "initialize-workspace", label: item.repo_source_kind === "local_authoring" ? "Import" : "Initialize" };
  }
}

function renderWorkspaceContentPanelForList(item) {
  return `
    <div class="workspace-content-panel" data-workspace-content-panel>
      ${renderWorkspaceContentPanel(state.workspaceContent, item, { showSnapshotReplace: true })}
    </div>
  `;
}

function renderWorkspaceReviewForm(item) {
  const hasExisting = Boolean(item.review_provider);
  const diagnostics = lookupWorkspaceDiagnostics(item.id);
  return `
    <div class="workspace-review-form" style="margin-top:var(--space-md); padding-top:var(--space-md); border-top:1px solid var(--line);">
      ${hasExisting ? `
        <div class="form-readonly-strip" style="margin-bottom:var(--space-sm)">
          <span class="form-readonly-item"><span class="form-readonly-label">Current provider</span> ${escapeHTML(item.review_provider)}</span>
          <span class="form-readonly-item"><span class="form-readonly-label">Auth ref</span> ${escapeHTML(diagnostics?.review_auth_ref || "not set")}</span>
        </div>
      ` : ""}
      <form class="threads-create-form" data-workspace-review-form>
        <select name="review_provider">
          <option value="">Provider</option>
          <option value="gitlab" ${item.review_provider === "gitlab" ? "selected" : ""}>GitLab</option>
        </select>
        <input name="review_auth_ref" placeholder="Auth env var (e.g. GITLAB_TOKEN)" value="${escapeHTML(diagnostics?.review_auth_ref || "")}">
        <button class="button button--sm button--primary" type="button" data-action="submit-workspace-review" data-workspace-id="${escapeHTML(item.id)}">${hasExisting ? "Update" : "Configure"}</button>
        <button class="button button--sm button--ghost" type="button" data-action="cancel-workspace-review">Cancel</button>
      </form>
    </div>
  `;
}

function renderWorkspacePushPanel(item) {
  const branch = String(item.default_branch || "main").trim() || "main";
  const remoteURL = String(item.remote_url || "").trim();
  const commits = Array.isArray(item.recent_commits) ? item.recent_commits : [];

  // Find remote HEAD index — commits after it are unpublished
  const remoteIdx = commits.findIndex((c) => c.is_remote_head);
  const unpublished = remoteIdx > 0 ? commits.slice(0, remoteIdx) : [];
  const localHead = commits.find((c) => c.is_head);
  const remoteHead = commits.find((c) => c.is_remote_head);

  const pushedHead = String(item.last_pushed_head || "").trim();
  const verifiedHead = String(item.last_verified_target_head || "").trim();
  const inSync = verifiedHead && pushedHead && verifiedHead === pushedHead;

  let syncLabel = "";
  if (inSync) {
    syncLabel = `${escapeHTML(branch)} in sync`;
  } else if (item.last_pushed_at) {
    const ago = timeAgo(item.last_pushed_at);
    syncLabel = `Last pushed ${ago === "now" ? ago : ago + " ago"}`;
  } else {
    syncLabel = "Never pushed";
  }

  const commitRows = unpublished.length
    ? unpublished.map((c) => {
        const hash = shortCommit(c.short_commit || c.commit || "");
        const when = c.committed_at ? timeAgo(c.committed_at) : "";
        return `<div class="entity-row__detail" style="padding-left:var(--space-sm)">${escapeHTML(hash)} · ${escapeHTML(c.subject || "")}${when ? ` · ${escapeHTML(when === "now" ? when : when + " ago")}` : ""}</div>`;
      }).join("")
    : `<div class="entity-row__detail" style="padding-left:var(--space-sm)">No unpublished commits.</div>`;

  return `
    <div class="workspace-review-form" style="margin-top:var(--space-md); padding-top:var(--space-md); border-top:1px solid var(--line);">
      <div class="entity-row__detail" style="margin-bottom:var(--space-xs)"><strong>Push ${escapeHTML(branch)}</strong> → origin${remoteURL ? ` (${escapeHTML(remoteURL)})` : ""}</div>
      <div class="entity-row__detail">Local HEAD: ${localHead ? escapeHTML(shortCommit(localHead.short_commit || localHead.commit || "")) : "unknown"}${remoteHead ? ` · Remote HEAD: ${escapeHTML(shortCommit(remoteHead.short_commit || remoteHead.commit || ""))}` : ""}</div>
      <div class="entity-row__detail" style="margin-bottom:var(--space-xs)">${syncLabel}</div>
      ${unpublished.length ? `<div class="entity-row__detail" style="margin-bottom:var(--space-xs)"><strong>${unpublished.length} unpublished commit${unpublished.length === 1 ? "" : "s"}:</strong></div>` : ""}
      ${commitRows}
      <div class="threads-create-form" style="margin-top:var(--space-sm)">
        <button class="button button--sm button--ghost" type="button" data-action="cancel-workspace-push">Cancel</button>
        <button class="button button--sm button--primary" type="button" data-action="submit-workspace-push" data-workspace-id="${escapeHTML(item.id)}">Push to origin</button>
      </div>
    </div>
  `;
}

function renderWorkspacePushStatus(item) {
  const branch = String(item.default_branch || "main").trim() || "main";
  const pushedHead = String(item.last_pushed_head || "").trim();
  const verifiedHead = String(item.last_verified_target_head || "").trim();
  const pushedAt = item.last_pushed_at ? formatTimestamp(item.last_pushed_at) : "";
  const pushedAgo = item.last_pushed_at ? timeAgo(item.last_pushed_at) : "";
  const details = [];
  if (pushedHead) details.push(`Last pushed ${shortCommit(pushedHead)}`);
  if (pushedAt && pushedAt !== "n/a") details.push(formatRelativeTimestampLabel(pushedAt, pushedAgo));
  if (verifiedHead && pushedHead && verifiedHead === pushedHead) {
    details.push(`${branch} in sync`);
  } else if (verifiedHead) {
    details.push(`Verified ${shortCommit(verifiedHead)}`);
  }
  if (!details.length) {
    if (item.remote_connection_state === "connected" && item.git_state === "ready") {
      return `<div class="entity-row__detail">No successful publish recorded for ${escapeHTML(branch)} yet.</div>`;
    }
    return "";
  }
  return `<div class="entity-row__detail">${escapeHTML(details.join(" · "))}</div>`;
}
function renderWorkspaceCommitHistory(item) {
  const commits = Array.isArray(item.recent_commits) ? item.recent_commits : [];
  if (!commits.length) return "";
  const rows = commits.map((entry) => {
    const markers = [];
    if (entry.is_head) markers.push("HEAD");
    if (entry.is_remote_head) markers.push("origin");
    const when = entry.committed_at ? timeAgo(entry.committed_at) : "";
    const labelParts = [];
    labelParts.push(shortCommit(entry.short_commit || entry.commit || ""));
    if (markers.length) labelParts.push(`[${markers.join(", ")}]`);
    if (entry.subject) labelParts.push(entry.subject);
    if (when) labelParts.push(when === "now" ? when : `${when} ago`);
    return `<div class="entity-row__detail">${escapeHTML(labelParts.join(" · "))}</div>`;
  }).join("");
  return `<div class="entity-row__detail">Recent commits</div>${rows}`;
}
function shortCommit(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.length <= 8 ? raw : raw.slice(0, 8);
}
function formatRelativeTimestampLabel(timestamp, ago) {
  if (ago === "now") return ago;
  if (ago) return `${ago} ago`;
  return timestamp;
}
function formatHarnessNote(harness) { if (harness.mode === "attached") return `Selected: ${harness.label} at ${harness.server_url}.`; return `Selected: ${harness.label}. Runtime manages the local host.`; }

function threadTitle(threadItem) { return threadItem.mode === "llm" ? `${lookupName(state.agents, threadItem.agent_id)} \u00b7 LLM` : `${lookupName(state.agents, threadItem.agent_id)} \u2192 ${lookupName(state.workspaces, threadItem.workspace_id)}`; }
function promotionsForThread(threadID) { return state.promotions.filter((item) => item.thread_id === threadID); }
function promotionModeLabel(mode) {
  if (mode === "remote_pr") return "Remote review";
  if (mode === "remote_push") return "Merge and push";
  return "Local merge";
}
function promotionStatusLabel(item) {
  if (item.state === "running" && item.backend_state) return String(item.backend_state).trim().replaceAll("_", " ");
  return String(item.state || "").trim().replaceAll("_", " ") || "queued";
}
function promotionBadgeTone(item) { return item.state === "succeeded" ? "succeeded" : item.state === "conflicted" ? "failed" : item.state === "failed" ? "failed" : item.state === "running" ? "running" : "queued"; }
function threadPreviewText(threadItem) {
  if (threadItem.mode === "llm") {
    if (threadItem.state === "running") return "Thinking...";
    if (threadItem.last_user_message) return compactPrompt(threadItem.last_user_message);
    if (threadItem.last_turn_state) return `Last turn ${threadItem.last_turn_state}`;
    return "No messages yet.";
  }
  if (threadItem.state === "ready_to_merge") return "Draft committed and ready for promotion.";
  if (threadItem.state === "merge_conflicted") return "Promotion conflicted. Continue in this thread, then retry.";
  if (threadItem.state === "merging") return "Promotion in progress.";
  if (threadItem.state === "awaiting_remote_merge") return "Waiting for remote merge.";
  if (threadItem.state === "recovery_required" && threadItem.latest_promotion?.mode === "remote_push") return "Remote push failed after local merge. Retry the push or reconcile manually.";
  if (threadItem.state === "archived") return "Archived after promotion.";
  const pending = latestPendingDecisionForThread(threadItem.id);
  if (pending) return `Needs input: ${compactPrompt(pending.prompt || pending.summary)}`;
  if (threadItem.state === "recovery_required" && threadItem.recovery_hint) return threadItem.recovery_hint;
  if (threadItem.last_user_message) return compactPrompt(threadItem.last_user_message);
  if (threadItem.last_turn_state) return `Last turn ${threadItem.last_turn_state}`;
  return "No messages yet.";
}
function latestPendingDecisionForThread(threadID) { return state.pendingDecisions.find((item) => item.thread_id === threadID) || null; }
function lookupDecisionTurn(turnID) {
  const normalizedTurnID = String(turnID || "");
  if (!normalizedTurnID) return null;
  if (state.turnDetail.turn && state.turnDetail.turn.id === normalizedTurnID) return state.turnDetail.turn;
  const threadTurn = state.threadDetail.turns.find((current) => current.id === normalizedTurnID);
  if (threadTurn) return threadTurn;
  return state.turns.find((current) => current.id === normalizedTurnID) || null;
}
function mergeTurnRecord(currentTurn, nextTurn) { if (!nextTurn || !nextTurn.id) return currentTurn; if (!currentTurn || !currentTurn.id) return nextTurn; return { ...currentTurn, ...nextTurn, state: resolveRunState(currentTurn.state, nextTurn.state) }; }

function mergeTurnsIntoGlobalList(turns) {
  if (!Array.isArray(turns) || turns.length === 0) return;
  const mergedByID = new Map((state.turns || []).map((item) => [item.id, item]));
  for (const turnItem of turns) { if (!turnItem || !turnItem.id) continue; mergedByID.set(turnItem.id, mergeTurnRecord(mergedByID.get(turnItem.id) || null, turnItem)); }
  state.turns = sortTurnsDescending([...mergedByID.values()]);
}

function syncTurnStateFromEvent(turnID, nextState) {
  if (!turnID || !nextState) return;
  state.turns = state.turns.map((item) => item.id === turnID ? { ...item, state: resolveRunState(item.state, nextState) } : item);
  if (state.threadDetail.turns.length > 0) state.threadDetail.turns = state.threadDetail.turns.map((item) => item.id === turnID ? { ...item, state: resolveRunState(item.state, nextState) } : item);
}
function sameTurnSummary(left, right) { return JSON.stringify((left || []).map((item) => [item.id, item.state, item.thread_id, item.wait_state || "", item.pending_decision_id || "", item.finished_at || "", item.updated_at || ""])) === JSON.stringify((right || []).map((item) => [item.id, item.state, item.thread_id, item.wait_state || "", item.pending_decision_id || "", item.finished_at || "", item.updated_at || ""])); }
function sameThreadSummary(left, right) { return JSON.stringify((left || []).map((item) => [item.id, item.state, item.last_turn_id || "", item.last_head_commit || "", item.updated_at || ""])) === JSON.stringify((right || []).map((item) => [item.id, item.state, item.last_turn_id || "", item.last_head_commit || "", item.updated_at || ""])); }
function sameDecisionSummary(left, right) { return JSON.stringify((left || []).map((item) => [item.id, item.state, item.turn_id || "", item.updated_at || ""])) === JSON.stringify((right || []).map((item) => [item.id, item.state, item.turn_id || "", item.updated_at || ""])); }
function samePromotionSummary(left, right) { return JSON.stringify((left || []).map((item) => [item.id, item.state, item.backend_state || "", item.finished_at || "", item.updated_at || ""])) === JSON.stringify((right || []).map((item) => [item.id, item.state, item.backend_state || "", item.finished_at || "", item.updated_at || ""])); }
